import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAdminEventFilter } from '@/hooks/useAdminEventFilter';
import { Loader2, Search, Building2, X, Mail, Globe, Phone, CheckCircle, XCircle, Calendar, Briefcase, Star, Edit, Filter, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { displayName } from '@/lib/utils';
import { EXHIBITOR_ORG_TYPES, PARTNER_ORG_TYPES, TECHNOLOGY_AREAS, TECHNOLOGY_AREA_LABEL_MAP, SEEKING_LABEL_MAP } from '@/lib/constants';
import { getOrgTypeBucket } from '@/lib/partnerOrgBucket';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import type { Registration, RegistrationStatus } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface PartnersResponse {
  success: boolean;
  data: Registration[];
  counts?: Record<string, number>;
  pagination?: { total: number; limit: number; offset: number };
}

const STATUS_STYLES: Record<RegistrationStatus, { color: string; bg: string; label: string }> = {
  pending: { color: '#ca8a04', bg: 'bg-yellow-50 dark:bg-yellow-950', label: 'Pending' },
  approved: { color: '#16a34a', bg: 'bg-green-50 dark:bg-green-950', label: 'Approved' },
  rejected: { color: '#dc2626', bg: 'bg-red-50 dark:bg-red-950', label: 'Rejected' },
};

type StatusFilterValue = RegistrationStatus | 'all';

const STATUS_FILTER_OPTIONS: { value: StatusFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const ORG_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  ...EXHIBITOR_ORG_TYPES.map((t) => ({ value: t.value, label: t.value })),
];

const PARTNER_ORG_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  PARTNER_ORG_TYPES.map((t) => [t.value, t.label]),
);

/** Display-friendly label for the partner badge / details panel. */
function getOrgTypeLabel(reg: Registration): string | null {
  const bucket = getOrgTypeBucket(reg);
  return bucket ? PARTNER_ORG_TYPE_LABELS[bucket] ?? null : null;
}

function isIndustryType(reg: Registration): boolean {
  return getOrgTypeBucket(reg) === 'industry';
}

type PartnerActionType = 'approve' | 'reject' | 'approve-exhibitor' | 'reject-exhibitor';

export default function PartnerManagementPage() {
  const { eventFilter, setEventFilter, events, fetchEvents, getResolvedEventId } = useAdminEventFilter();

  // Data state
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<Registration[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Registration | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [selectedOrgType, setSelectedOrgType] = useState<string>('all');

  // Dialog state
  const [actionLoading, setActionLoading] = useState(false);
  // Tracks which specific partner/exhibitor approval action is in flight, so only
  // the button being acted on shows a spinner (all four are disabled to prevent races).
  const [partnerAction, setPartnerAction] = useState<PartnerActionType | null>(null);


  // Edit modal state
  const [editDialog, setEditDialog] = useState(false);
  const [editPartnerId, setEditPartnerId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    websiteUrl: '',
    organizationType: '',
    collaborationPitch: '',
    researchAreas: [] as string[],
    pocFirstName: '',
    pocLastName: '',
    pocEmail: '',
    pocRank: '',
    isFeatured: false,
    isEventSponsor: false,
  });

  // Fetch partners (server-side event filtering, client-side for the rest)
  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      const eventId = getResolvedEventId();
      if (eventId) params.set('eventId', eventId);

      const res = await api.get<PartnersResponse>(`/admin/partners?${params}`);
      if (res.success) {
        setPartners(res.data);
        if (selectedPartner) {
          const updated = res.data.find((r) => r.id === selectedPartner.id);
          if (updated) setSelectedPartner(updated);
          else setSelectedPartner(null);
        }
      }
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [selectedPartner]);

  useEffect(() => {
    fetchEvents().then(() => fetchPartners());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when event filter changes (skip initial mount — handled above)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter]);

  // Client-side filtering (status, search, org type — all instant)
  const filteredPartners = useMemo(() => {
    return partners.filter((reg) => {
      if (statusFilter !== 'all' && reg.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !displayName(reg).toLowerCase().includes(q) &&
          !reg.email.toLowerCase().includes(q) &&
          !(reg.organization || '').toLowerCase().includes(q)
        ) return false;
      }
      if (selectedOrgType !== 'all') {
        if (selectedOrgType === 'Industry') {
          if (getOrgTypeBucket(reg) !== 'industry') return false;
        } else if (reg.exhibitorOrgType !== selectedOrgType) {
          return false;
        }
      }
      return true;
    });
  }, [partners, statusFilter, search, selectedOrgType]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedPartner && !filteredPartners.some((r) => r.id === selectedPartner.id)) {
      setSelectedPartner(null);
    }
  }, [filteredPartners, selectedPartner]);

  const filterCount = (search ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (selectedOrgType !== 'all' ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSelectedOrgType('all');
  };

  const openEditModal = (reg: Registration) => {
    if (!reg.partner) {
      toast.error('Partner record not found. Approve this registration first.');
      return;
    }
    const partner = reg.partner;
    setEditPartnerId(partner.id);
    // Pre-fill from Partner data, falling back to Registration for POC fields
    const pocFirstName = partner.pocFirstName || reg.firstName;
    const pocLastName = partner.pocLastName || reg.lastName;
    setEditForm({
      name: partner.name || reg.organization || displayName(reg),
      websiteUrl: partner.websiteUrl || reg.websiteUrl || '',
      organizationType: reg.exhibitorOrgType || partner.organizationType || '',
      collaborationPitch: partner.collaborationPitch || reg.bio || '',
      researchAreas: partner.researchAreas?.length ? partner.researchAreas : (reg.accelerationInterests || []),
      pocFirstName,
      pocLastName,
      pocEmail: partner.pocEmail || reg.email || '',
      pocRank: partner.pocRank || reg.rank || '',
      isFeatured: partner.isFeatured,
      isEventSponsor: partner.isEventSponsor || false,
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editPartnerId) return;
    setActionLoading(true);
    try {
      await api.patch(`/admin/partners/${editPartnerId}`, {
        name: editForm.name || undefined,
        websiteUrl: editForm.websiteUrl || null,
        organizationType: editForm.organizationType || null,
        collaborationPitch: editForm.collaborationPitch || null,
        researchAreas: editForm.researchAreas,
        pocFirstName: editForm.pocFirstName || null,
        pocLastName: editForm.pocLastName || null,
        pocEmail: editForm.pocEmail || null,
        pocRank: editForm.pocRank || null,
        isFeatured: editForm.isFeatured,
        isEventSponsor: editForm.isEventSponsor,
      });
      toast.success('Partner updated');
      setEditDialog(false);
      await fetchPartners();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to update partner');
    } finally {
      setActionLoading(false);
    }
  };

  const setEdit = (field: keyof typeof editForm, value: string | boolean | string[]) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const isPartnerApprovedForEvent = (reg: Registration | null): boolean => {
    if (!reg?.partner?.approvedEventIds || !reg.eventId) return false;
    return reg.partner.approvedEventIds.includes(reg.eventId);
  };

  const isExhibitorApprovedForEvent = (reg: Registration | null): boolean => {
    if (!reg?.partner?.exhibitorEventIds || !reg.eventId) return false;
    return reg.partner.exhibitorEventIds.includes(reg.eventId);
  };

  /**
   * Combined approval state for the list-view badge. A row is only fully
   * "Approved" when both the POC profile and the partner organization are
   * approved for the active event. Exhibitor approval is an additional
   * decision and does NOT factor into the list badge — that's about whether
   * the org appears on the public Industry Partners page.
   */
  const getCombinedStatus = (reg: Registration): RegistrationStatus => {
    if (reg.status === 'rejected') return 'rejected';
    if (reg.status === 'pending') return 'pending';
    return isPartnerApprovedForEvent(reg) ? 'approved' : 'pending';
  };

  const runPartnerAction = async (
    reg: Registration,
    endpoint: PartnerActionType,
    successMsg: string,
    errorMsg: string,
  ) => {
    if (!reg.partner) {
      toast.error('Partner record not found');
      return;
    }
    setPartnerAction(endpoint);
    try {
      await api.post(`/admin/partners/${reg.partner.id}/${endpoint}`, {});
      toast.success(successMsg);
      await fetchPartners();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || errorMsg);
    } finally {
      setPartnerAction(null);
    }
  };

  const handleApprovePartner = (reg: Registration) =>
    runPartnerAction(reg, 'approve', `${reg.partner?.name} approved as a partner`, 'Failed to approve partner');
  const handleRejectPartner = (reg: Registration) =>
    runPartnerAction(reg, 'reject', `${reg.partner?.name} partner approval withdrawn`, 'Failed to withdraw partner approval');
  const handleApproveExhibitor = (reg: Registration) =>
    runPartnerAction(reg, 'approve-exhibitor', `${reg.partner?.name} approved as an exhibitor`, 'Failed to approve exhibitor');
  const handleRejectExhibitor = (reg: Registration) =>
    runPartnerAction(reg, 'reject-exhibitor', `${reg.partner?.name} exhibitor approval withdrawn`, 'Failed to withdraw exhibitor approval');

  const toggleEditResearchArea = (value: string) => {
    setEditForm((prev) => {
      if (prev.researchAreas.includes(value)) {
        return { ...prev, researchAreas: prev.researchAreas.filter((v) => v !== value) };
      }
      if (prev.researchAreas.length >= 5) return prev;
      return { ...prev, researchAreas: [...prev.researchAreas, value] };
    });
  };


  // =====================
  // Column 1: Filters
  // =====================

  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            {filterCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-primary hover:underline">
                Clear ({filterCount})
              </button>
            )}
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Event Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event</Label>
            <RadioGroup value={eventFilter} onValueChange={(v) => { setEventFilter(v); setSelectedPartner(null); }} className="gap-0.5">
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="event-all" className="h-3.5 w-3.5" />
                <label htmlFor="event-all" className="text-xs cursor-pointer">All Events</label>
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="active" id="event-active" className="h-3.5 w-3.5" />
                <label htmlFor="event-active" className="text-xs cursor-pointer">Active Event</label>
              </div>
              {events
                .filter((ev) => ev.status === 'archived')
                .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime())
                .map((ev) => (
                  <div key={ev.id} className="flex items-center gap-1.5 py-0.5">
                    <RadioGroupItem value={ev.id} id={`event-${ev.id}`} className="h-3.5 w-3.5" />
                    <label htmlFor={`event-${ev.id}`} className="text-xs cursor-pointer flex items-center justify-between w-full">
                      <span className="truncate">{ev.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                        {new Date(ev.dateStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    </label>
                  </div>
                ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search partners..."
              className="h-8 pl-8 pr-8 text-xs"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          <Separator />

          {/* Status Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Status</Label>
            <RadioGroup value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilterValue)} className="gap-0.5">
              {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={value} id={`status-${value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`status-${value}`} className="text-xs cursor-pointer">{label}</label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Organization Type Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Organization Type</Label>
            <RadioGroup value={selectedOrgType} onValueChange={setSelectedOrgType} className="gap-0.5">
              {ORG_TYPE_FILTER_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={value} id={`orgtype-${value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`orgtype-${value}`} className="text-xs cursor-pointer">{label}</label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  // =====================
  // Column 2: Partner List
  // =====================

  const CardSkeleton = () => (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-border">
      <Skeleton className="w-9 h-9 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
    </div>
  );

  const ListPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Partners</h2>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${filteredPartners.length} applications`}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
        ) : filteredPartners.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No partners found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          filteredPartners.map((reg) => (
            <button
              key={reg.id}
              className={`w-full flex items-center gap-3 px-6 py-3 border-b border-border text-left transition-colors hover:bg-muted/50 ${
                selectedPartner?.id === reg.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
              }`}
              onClick={() => setSelectedPartner(reg)}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${
                isIndustryType(reg) ? 'bg-slate-700' : 'bg-sky-700'
              }`}>
                {isIndustryType(reg)
                  ? <Briefcase className="h-4 w-4" />
                  : <Building2 className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{reg.organization || displayName(reg)}</p>
                <p className="text-xs text-muted-foreground truncate">{displayName(reg)} &middot; {reg.email}</p>
              </div>
              {(() => {
                // Badge precedence: rejected POC > Exhibitor designation > Partner/Pending status.
                // Exhibitor takes precedence over "Approved" because it carries more info
                // (org has a booth) and is independent of Partner approval.
                if (reg.status === 'rejected') {
                  return (
                    <Badge
                      variant="outline"
                      className="text-[10px] flex-shrink-0"
                      style={{ borderColor: STATUS_STYLES.rejected.color, color: STATUS_STYLES.rejected.color }}
                    >
                      {STATUS_STYLES.rejected.label}
                    </Badge>
                  );
                }
                if (isExhibitorApprovedForEvent(reg)) {
                  return (
                    <Badge
                      variant="default"
                      className="text-[10px] flex-shrink-0 bg-tech-cyan hover:bg-tech-cyan/90 text-white border-transparent"
                    >
                      Exhibitor
                    </Badge>
                  );
                }
                const combined = getCombinedStatus(reg);
                return (
                  <Badge
                    variant="outline"
                    className="text-[10px] flex-shrink-0"
                    style={{ borderColor: STATUS_STYLES[combined].color, color: STATUS_STYLES[combined].color }}
                  >
                    {STATUS_STYLES[combined].label}
                  </Badge>
                );
              })()}
              <button
                type="button"
                className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                  reg.status === 'rejected'
                    ? 'text-muted-foreground/40 cursor-not-allowed'
                    : 'text-blue-600 hover:bg-blue-50'
                }`}
                title={reg.status === 'rejected' ? 'Partner rejected' : 'Edit partner'}
                onClick={(e) => { e.stopPropagation(); if (reg.status !== 'rejected' && reg.partner) openEditModal(reg); }}
              >
                <Edit className="h-3.5 w-3.5" />
              </button>
            </button>
          ))
        )}
      </div>
    </div>
  );

  // =====================
  // Column 3: Registration Details
  // =====================

  const sel = selectedPartner && filteredPartners.some((r) => r.id === selectedPartner.id) ? selectedPartner : null;

  const DetailsPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Partner Details</h2>
            <p className="text-sm text-muted-foreground truncate">{sel ? (sel.organization || displayName(sel)) : 'Select a partner'}</p>
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1">
        {sel ? (
          <div className="p-6 space-y-5 min-w-0">
            {/* Source Type Badge + Title */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="outline"
                  className="text-xs text-white border-0"
                  style={{ backgroundColor: isIndustryType(sel) ? '#334155' : '#0369a1' }}
                >
                  {isIndustryType(sel)
                    ? <Briefcase className="h-3 w-3 mr-1" />
                    : <Building2 className="h-3 w-3 mr-1" />}
                  {getOrgTypeLabel(sel) || 'Partner'}
                </Badge>
                {(() => {
                  const combined = getCombinedStatus(sel);
                  if (combined === 'approved') return null;
                  return (
                    <Badge variant="outline" className="text-xs" style={{ borderColor: STATUS_STYLES[combined].color, color: STATUS_STYLES[combined].color }}>
                      {STATUS_STYLES[combined].label}
                    </Badge>
                  );
                })()}
                {sel.partner?.isFeatured && (
                  <Badge className="text-xs bg-amber-500 text-white hover:bg-amber-600">
                    <Star className="h-3 w-3 mr-1" /> Featured
                  </Badge>
                )}
                {sel.partner?.isEventSponsor && (
                  <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800">
                    Event Sponsor
                  </Badge>
                )}
              </div>
              <h3 className="text-base font-bold leading-snug">{sel.partner?.name || sel.organization || displayName(sel)}</h3>
            </div>

            {/* Approval Status — three independent decisions:
                POC profile, Partner (event affiliation), Exhibitor (physical booth). */}
            {sel.partner && (() => {
              const pocApproved = sel.status === 'approved';
              const partnerApproved = isPartnerApprovedForEvent(sel);
              const exhibitorApproved = isExhibitorApprovedForEvent(sel);
              const pendingBadge = (
                <Badge variant="outline" className="text-[10px]" style={{ borderColor: STATUS_STYLES.pending.color, color: STATUS_STYLES.pending.color }}>Pending</Badge>
              );
              const approvedBadge = (
                <Badge variant="outline" className="text-[10px]" style={{ borderColor: STATUS_STYLES.approved.color, color: STATUS_STYLES.approved.color }}>Approved</Badge>
              );
              // Teal "Exhibitor" pill — same color used everywhere this concept appears
              // (column 2 list, profile cards on Network). Distinct from any other badge.
              const exhibitorBadge = (
                <Badge variant="default" className="text-[10px] bg-tech-cyan hover:bg-tech-cyan/90 text-white border-transparent">Exhibitor</Badge>
              );
              return (
                <Card className="p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground">Approval Status</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Point of Contact</span>
                      <Badge
                        variant="outline"
                        className="text-[10px]"
                        style={{ borderColor: STATUS_STYLES[sel.status].color, color: STATUS_STYLES[sel.status].color }}
                      >
                        {STATUS_STYLES[sel.status].label}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Partner organization</span>
                      {partnerApproved ? approvedBadge : pendingBadge}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Exhibitor (booth)</span>
                      {exhibitorApproved ? exhibitorBadge : pendingBadge}
                    </div>
                  </div>

                  {!pocApproved && (
                    <p className="text-xs text-muted-foreground">
                      Approve the POC's registration first on the Users page, then return here to approve the partner and/or exhibitor status.
                    </p>
                  )}

                  {pocApproved && (
                    <div className="space-y-2">
                      {partnerApproved ? (
                        <Button variant="destructive" size="sm" className="w-full" onClick={() => handleRejectPartner(sel)} disabled={partnerAction !== null}>
                          {partnerAction === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <XCircle className="h-3.5 w-3.5 mr-2" />}
                          Withdraw Partner Approval
                        </Button>
                      ) : (
                        <Button size="sm" className="w-full" onClick={() => handleApprovePartner(sel)} disabled={partnerAction !== null}>
                          {partnerAction === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <CheckCircle className="h-3.5 w-3.5 mr-2" />}
                          Approve as Partner
                        </Button>
                      )}
                      {exhibitorApproved ? (
                        <Button variant="destructive" size="sm" className="w-full" onClick={() => handleRejectExhibitor(sel)} disabled={partnerAction !== null}>
                          {partnerAction === 'reject-exhibitor' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <XCircle className="h-3.5 w-3.5 mr-2" />}
                          Withdraw Exhibitor Approval
                        </Button>
                      ) : (
                        <Button size="sm" className="w-full" onClick={() => handleApproveExhibitor(sel)} disabled={partnerAction !== null}>
                          {partnerAction === 'approve-exhibitor' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <CheckCircle className="h-3.5 w-3.5 mr-2" />}
                          Approve as Exhibitor
                        </Button>
                      )}
                    </div>
                  )}

                  {partnerApproved && (
                    <p className="text-xs text-muted-foreground">This partner is visible on the Industry Partners page.</p>
                  )}
                </Card>
              );
            })()}

            {/* Graceful fallback: exhibitor registration with no linked Partner record.
                Approval actions require a Partner, so explain rather than render nothing. */}
            {!sel.partner && (
              <Card className="p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">Approval Status</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Point of Contact</span>
                  <Badge
                    variant="outline"
                    className="text-[10px]"
                    style={{ borderColor: STATUS_STYLES[sel.status].color, color: STATUS_STYLES[sel.status].color }}
                  >
                    {STATUS_STYLES[sel.status].label}
                  </Badge>
                </div>
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    No partner organization record is linked to this registration, so partner and exhibitor approvals can&apos;t be managed here. A partner record is normally created when the exhibitor application is submitted — if this persists, the registration may need to be re-submitted or repaired.
                  </p>
                </div>
              </Card>
            )}

            {/* POC Contact Info */}
            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Point of Contact</p>
              <DetailRow label="Name" value={sel.partner ? `${sel.partner.pocFirstName || ''} ${sel.partner.pocLastName || ''}`.trim() || displayName(sel) : displayName(sel)} />
              <DetailRow icon={<Mail className="h-3.5 w-3.5" />} value={sel.partner?.pocEmail || sel.email} />
              {sel.phone && <DetailRow icon={<Phone className="h-3.5 w-3.5" />} value={sel.phone} />}
              {(sel.partner?.pocRank || sel.rank) && <DetailRow label="Title/Rank" value={sel.partner?.pocRank || sel.rank || ''} />}
              {sel.role && <DetailRow label="Role" value={sel.role} />}
              {sel.linkedinUrl && <DetailRow icon={<Globe className="h-3.5 w-3.5" />} value={sel.linkedinUrl} link />}
            </Card>

            {/* Organization Info */}
            <Card className="p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Organization</p>
              <DetailRow icon={<Building2 className="h-3.5 w-3.5" />} label="Name" value={sel.partner?.name || sel.organization || ''} />
              {getOrgTypeLabel(sel) && <DetailRow label="Type" value={getOrgTypeLabel(sel)!} />}
              {(sel.partner?.websiteUrl || sel.websiteUrl) && <DetailRow icon={<Globe className="h-3.5 w-3.5" />} label="Website" value={sel.partner?.websiteUrl || sel.websiteUrl || ''} link />}
              <DetailRow label="Workshop Interest" value={sel.workshopInterest ? 'Yes' : 'No'} />
              {sel.workshopDescription && (
                <div className="text-sm min-w-0">
                  <span className="text-muted-foreground">Workshop Description:</span>{' '}
                  <span className="whitespace-normal break-words">{sel.workshopDescription}</span>
                </div>
              )}
            </Card>

            {/* Collaboration Pitch */}
            {(sel.partner?.collaborationPitch || sel.bio) && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Collaboration Pitch</p>
                <p className="text-sm whitespace-normal break-words">{sel.partner?.collaborationPitch || sel.bio}</p>
              </Card>
            )}

            {/* Technology Areas */}
            {((sel.partner?.researchAreas && sel.partner.researchAreas.length > 0) || (sel.accelerationInterests && sel.accelerationInterests.length > 0)) && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Technology Areas</p>
                <div className="flex flex-wrap gap-1">
                  {(sel.partner?.researchAreas || sel.accelerationInterests || []).map((interest: string) => (
                    <Badge key={interest} variant="secondary" className="text-xs">{TECHNOLOGY_AREA_LABEL_MAP[interest] || interest}</Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Collaborative Projects */}
            {sel.projectData && Array.isArray(sel.projectData) && sel.projectData.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">Collaborative Projects ({sel.projectData.length})</p>
                {sel.projectData.map((pd, idx) => {
                  // Exhibitor projects may have additional fields from the registration form JSON
                  const raw = pd as Record<string, unknown>;
                  const seekingCollab = (raw.seekingCollaboration ?? raw.seeking ?? []) as string[];
                  const isCrada = raw.isCrada as boolean | undefined;
                  return (
                    <Card key={idx} className="p-4 space-y-2">
                      <p className="text-sm font-medium">{pd.title}</p>
                      <p className="text-sm text-muted-foreground">{pd.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {pd.classification && <Badge variant="outline" className="text-[10px]">{pd.classification}</Badge>}
                        {pd.stage && <Badge variant="outline" className="text-[10px]">{pd.stage}</Badge>}
                        {isCrada && <Badge className="text-[10px] bg-blue-500 text-white">CRADA</Badge>}
                      </div>
                      {seekingCollab.length > 0 && (
                        <div className="mt-1">
                          <p className="text-[10px] text-muted-foreground font-medium">Seeking:</p>
                          <div className="flex flex-wrap gap-1">
                            {seekingCollab.map((s: string) => (
                              <Badge key={s} variant="secondary" className="text-[10px]">{SEEKING_LABEL_MAP[s] || s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Acknowledgements */}
            {sel.acknowledgements && Array.isArray(sel.acknowledgements) && sel.acknowledgements.length > 0 && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Acknowledgements</p>
                <div className="space-y-2">
                  {sel.acknowledgements.map((ack: string, i: number) => (
                    <div key={i} className="flex gap-2 items-start">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground">{ack}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Event */}
            {sel.event && (
              <Card className="p-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Event</p>
                <p className="text-sm font-medium">{sel.event.name}</p>
              </Card>
            )}

            {/* Review Info */}
            {sel.reviewedAt && (
              <Card className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Review</p>
                <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Reviewed" value={new Date(sel.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                {sel.reviewer && <DetailRow label="Reviewer" value={displayName(sel.reviewer)} />}
                {sel.rejectionReason && (
                  <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-950 text-sm">
                    <p className="text-xs font-medium text-red-800 dark:text-red-200">Rejection Reason</p>
                    <p className="text-red-700 dark:text-red-300 mt-1">{sel.rejectionReason}</p>
                  </div>
                )}
                {sel.adminMessage && (
                  <div className="mt-2 p-2 rounded bg-blue-50 dark:bg-blue-950 text-sm">
                    <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Info Request</p>
                    <p className="text-blue-700 dark:text-blue-300 mt-1">{sel.adminMessage}</p>
                  </div>
                )}
                {sel.adminNotes && (
                  <div className="mt-2 p-2 rounded bg-muted text-sm">
                    <p className="text-xs font-medium text-muted-foreground">Admin Notes</p>
                    <p className="mt-1">{sel.adminNotes}</p>
                  </div>
                )}
              </Card>
            )}

            {/* Submitted Date */}
            <Card className="p-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">Submitted</p>
              <p className="text-sm">{new Date(sel.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
            </Card>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-8 pt-24">
            <div className="text-center">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a partner</p>
              <p className="text-sm mt-1">Click any partner application to review details</p>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <DesktopShell>
      <div className="h-full flex flex-col">
        <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h1 className="text-2xl font-bold">Partners</h1>
        </div>

        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            left={FiltersPanel}
            center={ListPanel}
            right={DetailsPanel}
            leftWidth="260px"
            equalCenterRight
            connectedPanels={!!sel}
          />
        </div>
      </div>


      {/* Edit Partner Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>Update partner information.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {/* Organization */}
            <div className="space-y-1.5">
              <Label className="text-sm">Organization Name</Label>
              <Input value={editForm.name} onChange={(e) => setEdit('name', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Organization Type</Label>
              <Select value={editForm.organizationType} onValueChange={(v) => setEdit('organizationType', v)}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {EXHIBITOR_ORG_TYPES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Collaboration Pitch</Label>
              <Textarea value={editForm.collaborationPitch} onChange={(e) => setEdit('collaborationPitch', e.target.value)} rows={3} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Website URL</Label>
              <Input value={editForm.websiteUrl} onChange={(e) => setEdit('websiteUrl', e.target.value)} placeholder="https://..." />
            </div>

            {/* Technology Areas */}
            <div className="space-y-1.5">
              <Label className="text-sm">Technology Areas ({editForm.researchAreas.length}/5)</Label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto border rounded-md p-2">
                {TECHNOLOGY_AREAS.map(({ value, label }) => {
                  const selected = editForm.researchAreas.includes(value);
                  const atLimit = editForm.researchAreas.length >= 5 && !selected;
                  return (
                  <Badge
                    key={value}
                    variant={selected ? 'default' : 'outline'}
                    className={`text-xs ${atLimit ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => toggleEditResearchArea(value)}
                  >
                    {label}
                  </Badge>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* POC Info */}
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Point of Contact</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">First Name</Label>
                <Input value={editForm.pocFirstName} onChange={(e) => setEdit('pocFirstName', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Last Name</Label>
                <Input value={editForm.pocLastName} onChange={(e) => setEdit('pocLastName', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Email</Label>
                <Input value={editForm.pocEmail} onChange={(e) => setEdit('pocEmail', e.target.value)} type="email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Rank / Title</Label>
                <Input value={editForm.pocRank} onChange={(e) => setEdit('pocRank', e.target.value)} />
              </div>
            </div>

            <Separator />

            {/* Featured toggle */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editForm.isFeatured}
                onCheckedChange={(v) => setEdit('isFeatured', !!v)}
              />
              <Label className="text-sm">Featured Partner</Label>
            </div>

            {/* Event Sponsor toggle */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editForm.isEventSponsor}
                onCheckedChange={(v) => setEdit('isEventSponsor', !!v)}
              />
              <Label className="text-sm">Event Sponsor</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={actionLoading || !editForm.name.trim()}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DesktopShell>
  );
}

function DetailRow({ icon, label, value, link }: { icon?: React.ReactNode; label?: string; value: string; link?: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm min-w-0">
      {icon && <span className="text-muted-foreground flex-shrink-0">{icon}</span>}
      {label && <span className="text-muted-foreground flex-shrink-0">{label}:</span>}
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate min-w-0 flex-1">{value}</a>
      ) : (
        <span className="truncate min-w-0 flex-1">{value}</span>
      )}
    </div>
  );
}
